import React, { ReactNode, useState } from 'react';
import { AvButtonGroup, AvMenu, AvTooltip, ButtonOption } from '@fabric-ui/components';
import { Edit, EyeShow, Paint } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Button, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { useAvContext } from '../../../context/AvContextProvider';
import { PAGE_PATHS } from '../../../types';
import { CustomDashboardDto, UserPermissionType } from '../types/combined.types';
import { getParamFromURL } from './Clickthrough/utils';
import EditDashboardDetailsWindow from './EditDashboardDetailsWindow';

const eyeShowIcon = <EyeShow style={iconSize(20)} />;
const paintIcon = <Paint style={iconSize(20)} />;
const editIcon = <Edit style={{ ...iconSize(18), marginTop: '2px' }} />;

type SaveButtonsProps = {
  label: string;
  disabled?: boolean;
  tooltipText?: string;
  options: ButtonOption[];
};

type ButtonProps = {
  disabled?: boolean;
  clickHandler: () => void;
  label: ReactNode;
  tooltipText?: string;
  isEditModeOnly?: boolean;
};

const TOOLBAR_HEIGHT = 64;
export interface ToolbarProps {
  newData: any;
  filters: any;
  setFilters: any;
  isSaving?: boolean;
  title?: string;
  onCancel?: () => void;
  onSuccess: (info: Partial<CustomDashboardDto>) => void;
  saveButtonsProps?: SaveButtonsProps;
  menuOptions?: {
    title?: string;
    onClick?(...args: unknown[]): unknown;
  }[];
  toolbarButtons?: ButtonProps[];
  disabled?: boolean;
}
const DashboardToolbar: React.FC<ToolbarProps> = ({
  newData,
  onSuccess,
  onCancel,
  filters,
  setFilters,
  isSaving = false,
  saveButtonsProps,
  toolbarButtons = [],
  title = 'Dashboard',
  menuOptions,
  disabled = false,
}) => {
  const {
    userPermissions: { hasAllowedEditResourcePermission },
  } = useAvContext();
  const isEditMode = getParamFromURL<boolean>(filters.isEditMode);

  const initDashboardInfo: Partial<CustomDashboardDto> = {
    name: newData.name,
    description: newData.description,
    apps: newData.apps,
    dashboardReportPermission: newData.dashboardReportPermission,
  };

  const [anchorEl, setAnchorEl] = useState<Element>();
  const handleClick = ({ currentTarget }) => setAnchorEl(anchorEl ? undefined : currentTarget);

  const onChangeFilter = key => value => {
    setFilters({ ...filters, [key]: Array.isArray(value) ? value : [value] });
  };

  const isAllowedEdit = hasAllowedEditResourcePermission({ path: PAGE_PATHS.CUSTOM_DASHBOARDS });
  const theme: any = useTheme();
  return (
    <Box
      sx={{
        height: TOOLBAR_HEIGHT,
        backgroundColor: theme.palette.colors.neutrals[100],
        width: '100%',
        ...flex.justifyBetween,
        position: 'relative',
        borderBottom: '1px solid #E2E2EB',
        px: 2,
      }}>
      <AvTooltip title={`Edit ${title} Details`} disabled={!isEditMode}>
        {/* @ts-ignore */}
        <Button
          variant="filter"
          className="transparent"
          disabled={!isEditMode}
          onClick={handleClick}
          active={Boolean(anchorEl)}
          sx={{
            color: theme.palette.colors.neutrals[800],
            height: 'fit-content',
            alignSelf: 'center',
            '&.Mui-disabled': { color: theme.palette.text.primary },
          }}>
          <Box sx={{ fontWeight: 600, ...flex.row, gap: 1 }}>
            {newData.name} {isEditMode && editIcon}
          </Box>
        </Button>
      </AvTooltip>
      {isAllowedEdit && (newData.userPermissionType === UserPermissionType.editor || !newData.id || newData.predefined) && !disabled && (
        <>
          <ToggleButtonGroup
            color="white"
            sx={{
              ...flex.itemsCenter,
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              '&.MuiToggleButtonGroup-root .Mui-selected': {
                svg: {
                  color: theme.palette.colors.neutrals[600],
                },
              },
            }}
            size="small"
            value={isEditMode}
            exclusive
            onChange={() => onChangeFilter('isEditMode')(!isEditMode)}>
            <ToggleButton value>
              <AvTooltip title={`Edit ${title}`}>
                <Box sx={{ ...flex.col, width: '100%', height: '100%', zIndex: 1 }}>{paintIcon}</Box>
              </AvTooltip>
            </ToggleButton>
            <ToggleButton value={false}>
              <AvTooltip title={`View ${title}`}>
                <Box sx={{ ...flex.col, width: '100%', height: '100%' }}>{eyeShowIcon}</Box>
              </AvTooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ ...flex.itemsCenter, gap: 1, pr: 2 }}>
            {isEditMode && onCancel && (
              <Button size="small" onClick={onCancel}>
                Cancel
              </Button>
            )}
            {toolbarButtons
              .filter(btn => !btn.isEditModeOnly || isEditMode)
              .map(buttonProps => (
                <AvTooltip title={buttonProps.tooltipText} disabled={!buttonProps?.tooltipText}>
                  <Button size="small" variant="outlined" onClick={buttonProps.clickHandler} disabled={buttonProps?.disabled}>
                    {buttonProps.label}
                  </Button>
                </AvTooltip>
              ))}
            {(isEditMode || newData.predefined) &&
              (saveButtonsProps?.options.length === 1 ? (
                <AvTooltip title={saveButtonsProps?.tooltipText} disabled={!saveButtonsProps?.tooltipText}>
                  <div>
                    <Button
                      disabled={saveButtonsProps.options[0]?.disabled || isSaving}
                      size="small"
                      variant="contained"
                      onClick={saveButtonsProps.options[0].clickHandler}>
                      {saveButtonsProps?.label || 'Save'}
                    </Button>
                  </div>
                </AvTooltip>
              ) : (
                saveButtonsProps && (
                  <AvButtonGroup
                    // @ts-ignore
                    variant="contained"
                    size="small"
                    options={saveButtonsProps.options}
                    shouldShowGroups
                    shouldChangeMainLabel={false}
                    disabled={saveButtonsProps.disabled}
                  />
                )
              ))}
            {menuOptions && <AvMenu options={menuOptions} />}
          </Box>
        </>
      )}
      {anchorEl ? (
        <EditDashboardDetailsWindow
          title={title}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          initData={initDashboardInfo}
          onSuccess={onSuccess}
          disabled={disabled}
        />
      ) : null}
    </Box>
  );
};

export default DashboardToolbar;
